import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';

import { concatWithDot } from 'helpers/string';

import { DepartmentPosition } from './DepartmentPosition/DepartmentPosition';
import {
  LEVEL2_DEPARTMENT_NAMES,
  level2DepartmentValues,
  level3DepartmentValues,
  prefix,
  STAFF_ORGANIZATION_NAMES,
} from './utils/form';

import styles from './StaffOrganization.module.scss';

export const StaffOrganization: React.FC = () => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MANAGEMENT_AND_ORGANIZATION.STAFF_ORGANIZATION,
  );

  const firstLevelPosition = getValues(concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.LEVEL1_DEPARTMENT));

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('management.title')}
          badgeText={t('management.staff.title')}
          section={FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION}
        />
      </FormSectionCard>
      {/* 7.3.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.numberOfStaff')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.NUMBER_OF_STAFF)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.NUMBER_OF_STAFF)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.NUMBER_OF_STAFF)}
          minRows={4}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.3.2 */}
      <FormSectionCard disabled={!canAccess} title={t('management.staff.organizationalStructure')} collapsible>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormSectionFilledCard title={t('management.staff.firstLevel')} className={styles.sectionContainer}>
              <Grid container>
                <Grid item xs={6}>
                  <TextInputControl
                    label={t('management.staff.departmentPosition')}
                    control={control}
                    id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.LEVEL1_DEPARTMENT)}
                    name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.LEVEL1_DEPARTMENT)}
                    disabled={!canAccess}
                    maxLength={50}
                  />
                </Grid>
              </Grid>
            </FormSectionFilledCard>
          </Grid>
          <Grid item xs={12}>
            <FormSectionFilledCard
              title={t('management.staff.secondLevel', { firstLevelPosition })}
              className={styles.sectionContainer}
            >
              <FieldArrayComponent
                name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.LEVEL2_DEPARTMENT)}
                initialItem={level2DepartmentValues}
                buttonText={t('management.staff.department')}
                fieldArrayStyles={{
                  removeButton: styles.removeButton,
                  container: styles.container,
                  rowContainer: styles.rowContainer,
                  addButton: styles.addButton,
                }}
                disabled={!canAccess}
              >
                {(namePrefix) => (
                  <TextInputControl
                    control={control}
                    id={concatWithDot(namePrefix, LEVEL2_DEPARTMENT_NAMES.DEPARTMENT)}
                    name={concatWithDot(namePrefix, LEVEL2_DEPARTMENT_NAMES.DEPARTMENT)}
                    label={t('management.staff.departmentPosition')}
                    disabled={!canAccess}
                    maxLength={50}
                  />
                )}
              </FieldArrayComponent>
            </FormSectionFilledCard>
          </Grid>
          <Grid item xs={12}>
            <FormSectionFilledCard title={t('management.staff.thirdLevel')} className={styles.sectionContainer}>
              <FieldArrayComponent
                notRequired
                name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.LEVEL3_DEPARTMENT)}
                initialItem={level3DepartmentValues}
                buttonText={t('management.staff.department')}
                fieldArrayStyles={{ removeButton: styles.removeButton, addButton: styles.addButton }}
                disabled={!canAccess}
                maxLength={10}
              >
                {(namePrefix) => <DepartmentPosition name={namePrefix} disabled={!canAccess} />}
              </FieldArrayComponent>
            </FormSectionFilledCard>
          </Grid>
        </Grid>
      </FormSectionCard>
      {/* 7.3.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.skills')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SKILLS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SKILLS)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SKILLS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.3.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.additionalHires')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.ADDITIONAL_HIRES)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.ADDITIONAL_HIRES)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.ADDITIONAL_HIRES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.3.5 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.approaches')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.APPROACHES)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.APPROACHES)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.APPROACHES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.3.6 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.attractingDifficulties')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.ATTRACTING_DIFFICULTIES)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.ATTRACTING_DIFFICULTIES)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.ATTRACTING_DIFFICULTIES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.3.7 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.salaryPolicy')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SALARY_POLICY)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SALARY_POLICY)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.SALARY_POLICY)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.3.8 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.retentionDifficulties')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.RETENTION_DIFFICULTIES)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.RETENTION_DIFFICULTIES)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.RETENTION_DIFFICULTIES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.3.9 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.staff.developmentPrograms')}
        aiFieldName={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.DEVELOPMENT_PROGRAMS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.DEVELOPMENT_PROGRAMS)}
          name={concatWithDot(prefix, STAFF_ORGANIZATION_NAMES.DEVELOPMENT_PROGRAMS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
    </>
  );
};
